import React, { FC } from "react";
import { Link } from "react-router-dom";

const MailLink: FC<{ email: string; subject?: string; children?: React.ReactNode }> = ({
	email,
	subject,
	children,
}) => (
	<a
		href={`mailto:${email}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`}
		className="text-bblue font-SatoshiMedium hover:underline"
	>
		{children || email}
	</a>
);

type Faq = {
	question: string;
	answer: React.ReactNode;
};

const FAQS: Faq[] = [
	{
		question: "How do I reset my password?",
		answer:
			"From the sign-in screen, tap \"Forgot password\" and follow the one-time-code steps to set a new password.",
	},
	{
		question: "How do I invite my team to a workspace?",
		answer:
			"Workspace admins can invite teammates from Account settings inside the app. Invited members only see the projects and permissions assigned to their role.",
	},
	{
		question: "Where can I manage my subscription or billing?",
		answer: (
			<>
				Plan details and pricing live on our{" "}
				<Link to="/pricing" className="text-bblue font-SatoshiMedium hover:underline">
					Pricing page
				</Link>
				. For changes to an existing subscription or a billing question,
				email us and we'll help directly.
			</>
		),
	},
	{
		question: "How do I delete my account?",
		answer: (
			<>
				In the app, go to <strong>Profile → Danger zone → Delete account</strong>.
				You can also request deletion from the web without installing the app
				on our{" "}
				<Link
					to="/delete-account"
					className="text-bblue font-SatoshiMedium hover:underline"
				>
					account deletion page
				</Link>
				.
			</>
		),
	},
	{
		question: "How does Bankolé handle my data?",
		answer: (
			<>
				See our{" "}
				<Link to="/policy" className="text-bblue font-SatoshiMedium hover:underline">
					Privacy Policy
				</Link>{" "}
				for what we collect and how it's used, and our{" "}
				<Link to="/terms" className="text-bblue font-SatoshiMedium hover:underline">
					Terms of Use
				</Link>{" "}
				for the terms that govern the Services.
			</>
		),
	},
	{
		question: "Something looks broken — how do I report a bug?",
		answer: (
			<>
				Email{" "}
				<MailLink email="support@usebankole.com" subject="Bug report">
					support@usebankole.com
				</MailLink>{" "}
				with a short description, your account email, and, if you can, a
				screenshot — that helps us reproduce and fix it faster.
			</>
		),
	},
];

const SupportContent = () => {
	return (
		<section className="content-section bg-white px-6 py-14 md:py-20">
			<div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-10">
				<div className="md:w-1/2">
					<h2 className="text-2xl font-SatoshiBold text-headerblue mb-6">
						Frequently asked questions
					</h2>
					<div className="flex flex-col divide-y divide-pbg">
						{FAQS.map((faq) => (
							<div key={faq.question} className="py-5 first:pt-0">
								<h3 className="font-SatoshiBold text-headerblue">
									{faq.question}
								</h3>
								<p className="mt-2 font-SatoshiRegular text-[#475467] leading-relaxed">
									{faq.answer}
								</p>
							</div>
						))}
					</div>
				</div>

				<div className="md:w-1/2">
					<div className="bg-white border border-pbg rounded-xl px-6 md:px-8 py-8">
						<h2 className="text-xl font-SatoshiBold text-headerblue">
							Contact support
						</h2>
						<p className="mt-2 font-SatoshiRegular text-sm text-[#475467]">
							Can't find what you're looking for? Email our support team and
							we'll get back to you, typically within one business day.
						</p>
						<a
							href="mailto:support@usebankole.com"
							className="mt-6 inline-flex justify-center py-3 px-6 rounded-lg font-SatoshiMedium bg-bblue text-white hover:bg-blueShades-6"
						>
							Email support@usebankole.com
						</a>

						<div className="mt-8 pt-8 border-t border-pbg">
							<h3 className="font-SatoshiBold text-headerblue">
								More resources
							</h3>
							<ul className="mt-3 space-y-2 font-SatoshiRegular text-sm text-[#475467]">
								<li>
									<Link
										to="/policy"
										className="text-bblue font-SatoshiMedium hover:underline"
									>
										Privacy Policy
									</Link>
								</li>
								<li>
									<Link
										to="/terms"
										className="text-bblue font-SatoshiMedium hover:underline"
									>
										Terms of Use
									</Link>
								</li>
								<li>
									<Link
										to="/delete-account"
										className="text-bblue font-SatoshiMedium hover:underline"
									>
										Delete your account
									</Link>
								</li>
								<li>
									<Link
										to="/pricing"
										className="text-bblue font-SatoshiMedium hover:underline"
									>
										Pricing & plans
									</Link>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default SupportContent;
